import type { ChargeFrequency, CustomFieldDto, OfflineTransactionPaymentMethod, SubsidyType } from '~/swagger/portal/Api';

/** An account the bridge has already resolved and confirmed exists, named for the confirmation. */
export interface GenieChartOfAccount {
  id: string;
  name: string;
}

/**
 * A create the Genie chat proposes with every field the API requires, so it can be approved in a
 * confirmation modal rather than handed to a form. Values are already coerced and range-checked by
 * the bridge — anything it could not parse never becomes one of these, and opens the form instead.
 *
 * Fields the native forms default are optional here and defaulted the same way, so the model only
 * has to send what it actually knows. `chartOfAccount` is the exception: the bridge resolves it —
 * the model's choice or the company default — and verifies it exists before building one of these,
 * so an account that cannot be settled falls back to the form's own picker rather than reaching a
 * modal with no way forward. It carries the name because the bridge already looked it up.
 */
export type GenieLeaseCreateAction =
  | {
      kind: 'create-lease-charge';
      amount: number;
      dueDate: string;
      description: string;
      chartOfAccount: GenieChartOfAccount;
      companyCharge?: boolean;
      billDate?: string;
    }
  | {
      kind: 'create-lease-deposit';
      amount: number;
      dueDate: string;
      chartOfAccount: GenieChartOfAccount;
      description?: string;
      billDate?: string;
    }
  | {
      kind: 'create-recurring-charge';
      amount: number;
      startFrom: string;
      description: string;
      chargeFrequency?: ChargeFrequency;
      chartOfAccount: GenieChartOfAccount;
      companyCharge?: boolean;
      end?: string;
    }
  | {
      kind: 'create-recurring-payment';
      amount: number;
      startFrom: string;
      paymentMethod: OfflineTransactionPaymentMethod;
      tenantId: string;
      paymentFrequency?: ChargeFrequency;
      end?: string;
      description?: string;
      reference?: string;
    }
  | {
      kind: 'create-subsidy';
      subsidizerId: string;
      chartOfAccount: GenieChartOfAccount;
      subsidyType: SubsidyType;
      subsidyFrequency: ChargeFrequency;
      amount: number;
      effectiveDate: string;
      description: string;
      voucherNumber?: string;
      endDate?: string;
      ownerId?: string;
    };

/**
 * A magic tag's value type. Derived from the field-options discriminator minus `reference`, which
 * holds an uploaded file no action tag can carry (see the confirm modal's reference-field guard).
 */
export type MagicTagFieldType = Exclude<CustomFieldDto['options']['type'], 'reference'>;

/**
 * The attributes a create/update field action carries, kept in their always-string wire form. The
 * bridge only trims them and folds the `minValues`/`maxValues` vocabulary onto `minLength`/`maxLength`
 * (one stored pair the server validates as a character count on a scalar string field, an item count
 * when `multiple` is set). Everything else is coerced by the confirm modal, which is the only place
 * that reliably knows the field's type — supplied in the action for a create, fetched from the server
 * for an update — and so can read `min`/`max` as integers for a number field or dates for a datetime
 * one, and route `value` into the right slot.
 *
 * Field-level keys (`name`, `description`, `public`, `filterable`, `listable`) map onto
 * {@link CustomFieldDto}; the rest map onto its `options` bag.
 */
export interface GenieMagicTagFieldInputs {
  name?: string;
  description?: string;
  public?: string;
  filterable?: string;
  listable?: string;
  required?: string;
  multiple?: string;
  multiline?: string;
  rejectUnknown?: string;
  dateOnly?: string;
  minLength?: string;
  maxLength?: string;
  min?: string;
  max?: string;
  step?: string;
  allowedValues?: string;
  value?: string;
}

/**
 * A data-changing action the Genie chat proposes. Unlike the create-* prefills (which navigate to a
 * native form modal), these open a single confirmation modal that fetches the current state, shows a
 * before/after summary, and on approve executes the change via the existing lease contexts. The
 * AI supplies only the intent + target id + proposed new values; the UI fetches everything it needs
 * to render the diff and never trusts the AI for current state.
 */
type GenieLeaseActionKind =
  | GenieLeaseCreateAction
  | { kind: 'set-move-out-date'; moveOutDate: string }
  | { kind: 'end-lease' }
  | { kind: 'unenroll-damage-waiver' }
  | {
      kind: 'update-recurring-payment';
      recurringPaymentId: string;
      amount?: string;
      startFrom?: string;
      end?: string;
      description?: string;
    }
  | { kind: 'update-recurring-charge'; recurringChargeId: string; amount?: string; startFrom?: string; end?: string; description?: string }
  | { kind: 'update-subsidy'; subsidyId: string; amount?: string; effectiveDate?: string; endDate?: string; description?: string }
  /**
   * A forward change to a recurring charge or subsidy: end the current record on `effectiveDate` and
   * start a replacement at the new amount from it, in one approval. The old record stays on file as
   * history. `effectiveDate` and `amount` are required (the model only emits these when both hold);
   * `description` carries the current record's through when omitted. The confirm modal reads the
   * current record for the before column and the fields to carry, and runs the end then the create.
   */
  | { kind: 'supersede-recurring-charge'; recurringChargeId: string; effectiveDate: string; amount: string; description?: string }
  | { kind: 'supersede-subsidy'; subsidyId: string; effectiveDate: string; amount: string; description?: string }
  | { kind: 'change-lease-dates'; start?: string; end?: string }
  | { kind: 'send-lease-message'; body: string }
  | { kind: 'set-magic-tag'; customFieldId: string; value: string }
  | { kind: 'clear-magic-tag'; customFieldId: string }
  | ({ kind: 'create-magic-tag-field'; name: string; type: MagicTagFieldType } & GenieMagicTagFieldInputs)
  | ({ kind: 'update-magic-tag-field'; customFieldId: string; active?: string } & GenieMagicTagFieldInputs)
  | { kind: 'delete-magic-tag-field'; customFieldId: string };

/**
 * Any proposed action may also carry a free-form `message` the model wrote for the PM — its own
 * words about why it is proposing this change. The confirmation modal leads with it, above the
 * portal's own before/after summary. Absent when the model sent no note, in which case the modal
 * shows only its static copy.
 */
export type GenieLeaseAction = GenieLeaseActionKind & { message?: string };

/**
 * An action that opened a native form rather than a confirmation modal. Held while the PM works in
 * the form so its outcome can be reported once they save or leave.
 */
export interface GenieFormAction {
  /** Wire action name, e.g. `add-subsidy`. */
  name: string;
  /** Noun phrase used in the outcome text, e.g. `the subsidy`. */
  subject: string;
  /** Route the action navigated to. Leaving it without saving counts as a dismissal. */
  path: string;
}

export type GenieActionOutcome =
  | { kind: 'approved' }
  | { kind: 'rejected' }
  | { kind: 'failed'; reason: string }
  /** The PM was shown the suggestion but there was nothing to apply (already matches, target gone). */
  | { kind: 'skipped'; reason: string };

const describeLeaseAction = (action: GenieLeaseAction): string => {
  switch (action.kind) {
    case 'create-lease-charge':
      return 'the new lease charge';
    case 'create-lease-deposit':
      return 'the new deposit charge';
    case 'create-recurring-charge':
      return 'the new recurring charge';
    case 'create-recurring-payment':
      return 'the new recurring payment';
    case 'create-subsidy':
      return 'the new subsidy';
    case 'set-move-out-date':
      return `setting the move-out date to ${action.moveOutDate}`;
    case 'end-lease':
      return 'ending the lease';
    case 'unenroll-damage-waiver':
      return 'opting the lease out of the damage waiver';
    case 'update-recurring-payment':
      return `the recurring payment change (recurringPaymentId ${action.recurringPaymentId})`;
    case 'update-recurring-charge':
      return `the recurring charge change (recurringChargeId ${action.recurringChargeId})`;
    case 'update-subsidy':
      return `the subsidy change (subsidyId ${action.subsidyId})`;
    case 'supersede-recurring-charge':
      return `the recurring charge supersede (recurringChargeId ${action.recurringChargeId})`;
    case 'supersede-subsidy':
      return `the subsidy supersede (subsidyId ${action.subsidyId})`;
    case 'change-lease-dates':
      return 'the lease date change';
    case 'send-lease-message':
      return 'sending the message to the tenants';
    case 'set-magic-tag':
      return `setting this lease's magic tag (customFieldId ${action.customFieldId})`;
    case 'clear-magic-tag':
      return `clearing this lease's magic tag (customFieldId ${action.customFieldId})`;
    case 'create-magic-tag-field':
      return `creating the magic tag field "${action.name}" on every lease`;
    case 'update-magic-tag-field':
      return `the magic tag field change (customFieldId ${action.customFieldId})`;
    case 'delete-magic-tag-field':
      return `deleting the magic tag field for every lease (customFieldId ${action.customFieldId})`;
  }
};

/**
 * One-line outcome text sent back to the Genie chat so it can verify the change and carry on.
 *
 * Deliberately untranslated. The PM never sees these strings — the backend keeps action-result
 * messages out of the transcript — so they are read only by the model and must stay stable English
 * whatever locale the portal is running in.
 */
export const leaseActionOutcomeReport = (action: GenieLeaseAction, outcome: GenieActionOutcome): string => {
  const subject = describeLeaseAction(action);
  switch (outcome.kind) {
    case 'approved':
      return `Action result: PM approved ${subject}.`;
    case 'rejected':
      return `Action result: PM rejected ${subject}.`;
    case 'failed':
      return `Action result: ${subject} failed to apply — ${outcome.reason}.`;
    case 'skipped':
      return `Action result: ${subject} was not applied — ${outcome.reason}.`;
  }
};

/** Same contract as {@link leaseActionOutcomeReport}, for actions that opened a form. Untranslated. */
export const formActionOutcomeReport = (action: GenieFormAction, outcome: 'submitted' | 'dismissed'): string =>
  outcome === 'submitted'
    ? `Action result: PM reviewed and saved ${action.subject} (${action.name}).`
    : `Action result: PM closed the ${action.subject} form without saving (${action.name}).`;
